import { Component, Input, Output, EventEmitter, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

export interface ModalConfig {
  title?: string;
  size?: 'sm' | 'lg' | 'xl';
  centered?: boolean;
  backdrop?: boolean | 'static';
  keyboard?: boolean;
  closeButton?: boolean;
}

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, NgbModule],
  templateUrl: './modal.html',
  styleUrls: ['./modal.scss']
})
export class ModalComponent {
  /**
   * Modal configuration
   */
  @Input() config: ModalConfig = {
    title: 'Modal',
    size: 'lg',
    centered: true,
    backdrop: 'static',
    keyboard: false,
    closeButton: true
  };

  /**
   * Modal width (e.g., '500px', '50%', '600px')
   */
  @Input() width: string = '500px';

  /**
   * Modal height (e.g., '600px', 'auto', '80vh')
   */
  @Input() height: string = 'auto';

  /**
   * Modal header template/content
   */
  @Input() headerTemplate: TemplateRef<any> | null = null;
  @Input() headerContent: string | null = null;

  /**
   * Modal description subtitle
   */
  @Input() description: string | null = null;

  /**
   * Modal body template/content
   */
  @Input() bodyTemplate: TemplateRef<any> | null = null;
  @Input() bodyContent: string | null = null;

  /**
   * Modal footer template/content
   */
  @Input() footerTemplate: TemplateRef<any> | null = null;
  @Input() footerContent: string | null = null;

  /**
   * Footer button configuration
   */
  @Input() footerButtons: Array<{
    label: string;
    cssClass?: string;
    action?: () => void;
    dismiss?: boolean;
  }> = [];

  /**
   * Event emitted when close button is clicked
   */
  @Output() closeModal = new EventEmitter<void>();

  /**
   * Event emitted when backdrop is clicked (if closeOnBackdrop is true)
   */
  @Output() backdropClick = new EventEmitter<void>();

  /**
   * Close the modal - calls cancel button action if exists
   */
  onClose(): void {
    // Find cancel button (first button with label 'Cancel' or first button with dismiss: true)
    const cancelButton = this.footerButtons.find(btn => 
      btn.label?.toLowerCase() === 'cancel' || 
      (btn.dismiss && !btn.label?.toLowerCase().includes('confirm'))
    );
    
    // Execute cancel button action if it exists
    if (cancelButton && cancelButton.action) {
      cancelButton.action();
    }
    
    // Emit close event to dismiss modal
    this.closeModal.emit();
  }

  /**
   * Handle backdrop click - same behavior as close button
   */
  onBackdropClick(): void {
    if (this.config.backdrop !== 'static') {
      // Call the same close logic to invoke cancel action
      this.backdropClick.emit();
      this.onClose();
    }
  }

  /**
   * Handle footer button click
   */
  onButtonClick(button: any): void {
    if (button.action) {
      button.action();
    }
    if (button.dismiss) {
      this.onClose();
    }
  }
}
